import {
  CALL_ENDED_STATUS,
  CONTEXT_CARD_KIND,
  LiveAssistCard,
  LiveAssistCardPatch,
  LiveAssistCitation,
  LiveAssistContext,
  LiveAssistEvent,
  LiveAssistStatus,
  REPLAY_AVAILABLE_STATUS,
  SUPERSEDED_STATUS,
} from 'components/Alpha/LiveAssist/liveAssistContract';

export const MAX_UNPINNED_CARDS = 3;

export const CONTEXT_KIND = CONTEXT_CARD_KIND;

export type LiveAssistRating = 'up' | 'down';

export type LiveAssistConnection =
  | 'idle'
  | 'connecting'
  | 'open'
  | 'reconnecting'
  | 'ended'
  | 'failed'
  | 'unavailable';

export interface LiveAssistCardView extends LiveAssistCard {
  pinned: boolean;
  rating?: LiveAssistRating;
  seq: number;
  doneLabel?: string;
}

export type LiveAssistAskPhase = 'sending' | 'waiting' | 'streaming' | 'queued' | 'done' | 'failed';

export interface LiveAssistAskView {
  id: string;
  serverId?: string;
  question: string;
  answer: string;
  phase: LiveAssistAskPhase;
  error?: string;
  citations: LiveAssistCitation[];
  autoResent?: boolean;
  deferred?: boolean;
  sentOn?: number;
  waitFor?: number;
  queuedError?: string;
  timedOut?: boolean;
}

export interface LiveAssistState {
  connection: LiveAssistConnection;
  sessionId?: number;
  streamId?: string;
  streamReady: boolean;
  sessionSeq: number;
  context: LiveAssistContext;
  status?: LiveAssistStatus;
  replayAvailable: boolean;
  cards: LiveAssistCardView[];
  dismissed: string[];
  evicted: string[];
  asks: LiveAssistAskView[];
  seenSeq: number;
  announcement: string;
  announcementSeq: number;
  seq: number;
  callEnded: boolean;
  superseded: boolean;
  contextRefresh: boolean;
  asksSent: number;
  retiredAsks: string[];
}

export type LiveAssistMessage =
  | { type: 'event'; event: LiveAssistEvent }
  | { type: 'connection'; connection: LiveAssistConnection }
  | { type: 'dismiss'; id: string }
  | { type: 'pin'; id: string; pinned: boolean }
  | { type: 'rate'; id: string; rating?: LiveAssistRating }
  | {
      type: 'askSent';
      id: string;
      question: string;
      replaces?: string;
      autoResent?: boolean;
      deferred?: boolean;
    }
  | { type: 'askAccepted'; id: string; serverId?: string }
  | { type: 'askFailed'; id: string; error: string; timedOut?: boolean }
  | { type: 'askGone'; id: string; error: string }
  | { type: 'askQueueExpired'; id: string; error: string }
  | { type: 'askCutOff'; ids: string[] }
  | { type: 'callEnded' }
  | { type: 'refreshContext' }
  | { type: 'replayStarted' }
  | { type: 'seen' };

export const initialLiveAssistState: LiveAssistState = {
  connection: 'idle',
  streamReady: false,
  sessionSeq: 0,
  context: {},
  replayAvailable: false,
  cards: [],
  dismissed: [],
  evicted: [],
  asks: [],
  seenSeq: 0,
  announcement: '',
  announcementSeq: 0,
  seq: 0,
  callEnded: false,
  superseded: false,
  contextRefresh: false,
  asksSent: 0,
  retiredAsks: [],
};

const STATUS_ANNOUNCEMENTS: Record<LiveAssistStatus, string> = {
  listening: 'Alpha is listening',
  paused: 'Alpha paused listening',
  no_transcript: 'This call has no live transcript',
  budget_reached: 'Alpha reached its suggestion limit for this call',
};

export const ANSWERED_ANNOUNCEMENT = 'Alpha answered your question';
export const ASK_CUT_OFF_TEXT = "Alpha's answer was cut off.";
export const ASK_CALL_ENDED_TEXT = 'The call ended before Alpha answered.';
export const CALL_ENDED_ANNOUNCEMENT = 'The call ended, so Alpha stopped listening';
export const SUPERSEDED_TEXT = 'Live Assist moved to another window.';
export const ASK_SUPERSEDED_TEXT = 'Live Assist moved to another window before Alpha answered.';

const announce = (state: LiveAssistState, text: string): LiveAssistState => ({
  ...state,
  announcement: text,
  announcementSeq: state.announcementSeq + 1,
});

const isNudge = (card: LiveAssistCardView): boolean => card.kind !== CONTEXT_KIND && !card.pinned;

const withinLimit = (state: LiveAssistState): LiveAssistState => {
  const nudges = state.cards.filter(isNudge).sort((left, right) => left.seq - right.seq);
  const overflow = nudges.slice(0, Math.max(0, nudges.length - MAX_UNPINNED_CARDS));
  if (overflow.length === 0) return state;
  return {
    ...state,
    cards: state.cards.filter(card => !overflow.includes(card)),
    evicted: [...state.evicted, ...overflow.map(card => card.id)],
  };
};

const rebuildContext = (state: LiveAssistState, card: LiveAssistCard): LiveAssistState => ({
  ...state,
  contextRefresh: false,
  cards: state.cards.map(item =>
    item.id === card.id
      ? { ...card, pinned: card.pinned ?? item.pinned, rating: item.rating, seq: item.seq }
      : item,
  ),
});

const addCard = (state: LiveAssistState, card: LiveAssistCard): LiveAssistState => {
  if (state.dismissed.includes(card.id) || state.evicted.includes(card.id)) return state;
  if (state.cards.some(item => item.id === card.id)) {
    return card.kind === CONTEXT_KIND && state.contextRefresh ? rebuildContext(state, card) : state;
  }
  const seq = state.seq + 1;
  const added: LiveAssistCardView = { ...card, pinned: card.pinned ?? false, seq };
  return announce(
    withinLimit({ ...state, seq, cards: [...state.cards, added] }),
    `New from Alpha: ${card.title}`,
  );
};

const markDone = (state: LiveAssistState, id: string, label?: string): LiveAssistState =>
  state.cards.some(card => card.id === id)
    ? {
        ...state,
        cards: state.cards.map(card =>
          card.id === id ? { ...card, card_required: undefined, doneLabel: label ?? '' } : card,
        ),
      }
    : state;

const patchCard = (state: LiveAssistState, patch: LiveAssistCardPatch): LiveAssistState => {
  if (!state.cards.some(card => card.id === patch.id)) return state;
  return {
    ...state,
    cards: state.cards.map(card =>
      card.id === patch.id ? { ...card, ...patch, pinned: patch.pinned ?? card.pinned } : card,
    ),
  };
};

const ANSWERING_PHASES: LiveAssistAskPhase[] = ['sending', 'waiting', 'streaming'];
const OPEN_PHASES: LiveAssistAskPhase[] = [...ANSWERING_PHASES, 'queued'];
const UNANSWERED_PHASES: LiveAssistAskPhase[] = ['sending', 'waiting', 'queued'];

const isOpen = (ask: LiveAssistAskView | undefined): ask is LiveAssistAskView =>
  ask !== undefined && OPEN_PHASES.includes(ask.phase);

const isAnswering = (ask: LiveAssistAskView | undefined): ask is LiveAssistAskView =>
  ask !== undefined && ANSWERING_PHASES.includes(ask.phase);

const takesAnswer = (ask: LiveAssistAskView | undefined): ask is LiveAssistAskView =>
  ask !== undefined &&
  (ANSWERING_PHASES.includes(ask.phase) || (ask.phase === 'failed' && ask.timedOut === true));

const revive = (ask: LiveAssistAskView): LiveAssistAskView =>
  ask.timedOut === true ? { ...ask, error: undefined, timedOut: undefined } : ask;

const lose = (ask: LiveAssistAskView, error: string, sessionSeq: number): LiveAssistAskView =>
  ask.autoResent === true
    ? { ...ask, phase: 'failed', error }
    : { ...ask, phase: 'queued', queuedError: error, waitFor: ask.sentOn ?? sessionSeq };

const loseAnswers = (state: LiveAssistState): LiveAssistState => {
  const hit = (ask: LiveAssistAskView): boolean =>
    ask.phase === 'waiting' || ask.phase === 'streaming';
  if (!state.asks.some(hit)) return state;
  return {
    ...state,
    asks: state.asks.map(ask => (hit(ask) ? lose(ask, ASK_CUT_OFF_TEXT, state.sessionSeq) : ask)),
  };
};

const NO_STREAM: Pick<LiveAssistState, 'streamId' | 'streamReady'> = {
  streamId: undefined,
  streamReady: false,
};

const targetAsk = (
  state: LiveAssistState,
  event: { clientAskId?: string; askId?: string },
): string | undefined => {
  const { asks, retiredAsks } = state;
  if (event.clientAskId !== undefined) {
    if (retiredAsks.includes(event.clientAskId)) return undefined;
    const match = asks.find(ask => ask.id === event.clientAskId);
    return takesAnswer(match) ? match.id : undefined;
  }
  if (event.askId !== undefined) {
    if (retiredAsks.includes(event.askId)) return undefined;
    const match = asks.find(ask => ask.serverId === event.askId);
    return takesAnswer(match) ? match.id : undefined;
  }
  if (state.asksSent !== 1) return undefined;
  return takesAnswer(asks[0]) ? asks[0].id : undefined;
};

const endAsks = (state: LiveAssistState, reason = ASK_CALL_ENDED_TEXT): LiveAssistState => {
  const cut = cutOff(state);
  if (!cut.asks.some(ask => UNANSWERED_PHASES.includes(ask.phase))) return cut;
  return {
    ...cut,
    asks: cut.asks.map(ask =>
      UNANSWERED_PHASES.includes(ask.phase) ? { ...ask, phase: 'failed', error: reason } : ask,
    ),
  };
};

const cutOff = (state: LiveAssistState, ids?: string[]): LiveAssistState => {
  const hit = (ask: LiveAssistAskView): boolean =>
    ask.phase === 'streaming' && (ids === undefined || ids.includes(ask.id));
  if (!state.asks.some(hit)) return state;
  return {
    ...state,
    asks: state.asks.map(ask =>
      hit(ask) ? { ...ask, phase: 'failed', error: ASK_CUT_OFF_TEXT } : ask,
    ),
  };
};

const updateAsk = (
  state: LiveAssistState,
  id: string | undefined,
  update: (ask: LiveAssistAskView) => LiveAssistAskView,
): LiveAssistState =>
  id === undefined
    ? state
    : { ...state, asks: state.asks.map(ask => (ask.id === id ? update(ask) : ask)) };

const applyStatus = (state: LiveAssistState, status: LiveAssistStatus): LiveAssistState => {
  if (state.status === status) return state;
  const next = { ...state, status };
  return state.status === undefined ? next : announce(next, STATUS_ANNOUNCEMENTS[status]);
};

const applyEvent = (state: LiveAssistState, event: LiveAssistEvent): LiveAssistState => {
  switch (event.type) {
    case 'session_started':
      return {
        ...state,
        sessionId: event.sessionId ?? state.sessionId,
        streamId: event.streamId,
        streamReady: true,
        sessionSeq: state.sessionSeq + 1,
        context: event.context,
        replayAvailable: state.replayAvailable || event.replayAvailable,
      };
    case 'card':
      return addCard(state, event.card);
    case 'card_update':
      return patchCard(state, event.card);
    case 'card_removed':
      return { ...state, cards: state.cards.filter(card => card.id !== event.id) };
    case 'card_done':
      return markDone(state, event.id, event.label);
    case 'ask_delta':
      return updateAsk(state, targetAsk(state, event), ask => ({
        ...revive(ask),
        answer: ask.answer + event.text,
        phase: 'streaming',
      }));
    case 'ask_done': {
      const id = targetAsk(state, event);
      if (id === undefined) return state;
      return announce(
        updateAsk(state, id, ask => ({
          ...revive(ask),
          phase: 'done',
          citations: event.citations,
        })),
        ANSWERED_ANNOUNCEMENT,
      );
    }
    case 'status':
      if (event.status === REPLAY_AVAILABLE_STATUS) return { ...state, replayAvailable: true };
      if (event.status === CALL_ENDED_STATUS) {
        return state.callEnded
          ? state
          : announce({ ...state, callEnded: true }, CALL_ENDED_ANNOUNCEMENT);
      }
      if (event.status === SUPERSEDED_STATUS) {
        return state.superseded
          ? state
          : announce(
              endAsks(
                { ...state, ...NO_STREAM, superseded: true, connection: 'ended' },
                ASK_SUPERSEDED_TEXT,
              ),
              SUPERSEDED_TEXT,
            );
      }
      return applyStatus(state, event.status);
    default:
      return state;
  }
};

export const liveAssistReducer = (
  state: LiveAssistState,
  message: LiveAssistMessage,
): LiveAssistState => {
  switch (message.type) {
    case 'event':
      return applyEvent(state, message.event);
    case 'connection': {
      if (
        state.connection === message.connection ||
        state.connection === 'unavailable' ||
        state.superseded
      ) {
        return state;
      }
      const next = { ...state, connection: message.connection };
      return message.connection === 'open' ? next : loseAnswers({ ...next, ...NO_STREAM });
    }
    case 'callEnded': {
      if (state.connection === 'unavailable') return state;
      const ended = endAsks({
        ...state,
        callEnded: true,
        connection: 'ended',
        ...NO_STREAM,
      });
      return state.callEnded ? ended : announce(ended, CALL_ENDED_ANNOUNCEMENT);
    }
    case 'replayStarted':
      return state.connection === 'unavailable'
        ? state
        : { ...state, ...NO_STREAM, callEnded: false, connection: 'idle' };
    case 'askCutOff':
      return cutOff(state, message.ids);
    case 'refreshContext':
      return state.contextRefresh ? state : { ...state, contextRefresh: true };
    case 'dismiss':
      return {
        ...state,
        cards: state.cards.filter(card => card.id !== message.id),
        dismissed: state.dismissed.includes(message.id)
          ? state.dismissed
          : [...state.dismissed, message.id],
      };
    case 'pin':
      return {
        ...state,
        cards: state.cards.map(card =>
          card.id === message.id ? { ...card, pinned: message.pinned } : card,
        ),
      };
    case 'rate':
      return {
        ...state,
        cards: state.cards.map(card =>
          card.id === message.id ? { ...card, rating: message.rating } : card,
        ),
      };
    case 'askSent': {
      const deferred = message.deferred === true;
      const fresh: LiveAssistAskView = {
        id: message.id,
        question: message.question,
        answer: '',
        phase: deferred ? 'queued' : 'sending',
        citations: [],
        autoResent: message.autoResent === true,
        ...(deferred ? { deferred, waitFor: state.sessionSeq } : {}),
        ...(!deferred && state.streamReady ? { sentOn: state.sessionSeq } : {}),
      };
      const old = state.asks.find(ask => ask.id === message.replaces);
      const retired =
        old === undefined
          ? state.retiredAsks
          : [...state.retiredAsks, old.id, ...(old.serverId !== undefined ? [old.serverId] : [])];
      return {
        ...state,
        asksSent: state.asksSent + 1,
        retiredAsks: retired,
        asks:
          old === undefined
            ? [...state.asks, fresh]
            : state.asks.map(ask => (ask.id === old.id ? fresh : ask)),
      };
    }
    case 'askAccepted':
      return updateAsk(state, message.id, ask => {
        const accepted = {
          ...ask,
          ...(message.serverId !== undefined ? { serverId: message.serverId } : {}),
        };
        if (ask.phase !== 'sending') return accepted;
        const gone =
          ask.sentOn !== undefined && (!state.streamReady || state.sessionSeq !== ask.sentOn);
        return gone
          ? lose(accepted, ASK_CUT_OFF_TEXT, state.sessionSeq)
          : { ...accepted, phase: 'waiting' };
      });
    case 'askFailed':
      return updateAsk(state, message.id, ask =>
        isOpen(ask)
          ? {
              ...ask,
              phase: 'failed',
              error: message.error,
              ...(message.timedOut === true ? { timedOut: true } : {}),
            }
          : ask,
      );
    case 'askGone':
      return updateAsk(state, message.id, ask =>
        isAnswering(ask) ? lose(ask, message.error, state.sessionSeq) : ask,
      );
    case 'askQueueExpired':
      return updateAsk(state, message.id, ask =>
        ask.phase === 'queued'
          ? { ...ask, phase: 'failed', error: ask.queuedError ?? message.error }
          : ask,
      );
    case 'seen':
      return state.seenSeq === state.seq ? state : { ...state, seenSeq: state.seq };
    default:
      return state;
  }
};

export const asksToResend = (state: LiveAssistState): LiveAssistAskView[] =>
  state.streamReady
    ? state.asks.filter(ask => ask.phase === 'queued' && state.sessionSeq > (ask.waitFor ?? 0))
    : [];

export const unseenCount = (state: LiveAssistState): number =>
  state.cards.filter(card => card.seq > state.seenSeq).length;

const bySeqDesc = (left: LiveAssistCardView, right: LiveAssistCardView): number =>
  right.seq - left.seq;

export const orderedCards = (state: LiveAssistState): LiveAssistCardView[] => {
  const context = state.cards
    .filter(card => card.kind === CONTEXT_KIND)
    .sort((left, right) => left.seq - right.seq);
  const rest = state.cards.filter(card => card.kind !== CONTEXT_KIND);
  return [
    ...context,
    ...rest.filter(card => card.pinned).sort(bySeqDesc),
    ...rest.filter(card => !card.pinned).sort(bySeqDesc),
  ];
};
